import React, { useState, useEffect, useRef, createContext, useContext, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';

// VIEWS
import Auth from "./Views/Auth/Auth";
import HomePage from './Views/HomePage/HomePage';
import PageHeader from './Views/PageHeader/PageHeader';
import PageFooter from './Views/PageFooter/PageFooter';
import WhatIsQuoll from './Views/WhatIsQuoll/WhatIsQuoll';
import WhatsInEachAnalysis from './Views/WhatsInEachAnalysis/WhatsInEachAnalysis';
import CommonMisconceptions from './Views/CommonMisconceptions/CommonMisconceptions';
import Resources from './Views/Resouces/Resouces';
import WhyFree from './Views/WhyFree/WhyFree';
import AddressSearchProgress from './Views/AddressSearchProgress/AddressSearchProgress';
import PropertyExplorer from './Views/PropertyExplorer/PropertyExplorer';
import ErrorPage from './Views/ErrorPage/ErrorPage';
import ContactUs from './Views/ContactUs/ContactUs';
import WorkWithUs from './Views/WorkWithUs/WorkWithUs';
import Methodology from './Views/Methodology/Methodology';
import { Blog } from './Views/Blog/Blog';
import ErrorBoundary from './Views/ErrorBoundary/ErrorBoundary';
import AddressSearchHandler from './Views/AddressSearchHandler/AddressSearchHandler';
import Faq from './Views/Faq/Faq';
import "./App.css";

// SERVICES
import { v4 as uuidv4 } from "uuid";
import ReactGA4 from 'react-ga4';
import { QGDStorage, ThumbtackStorage, PortalStorage } from './services/QGDStorage';
import { parseApiResponse, validatePortalContent } from '/src/services/ParseApiResponse';
import Hotjar from '@hotjar/browser';
import { GoogleOAuthProvider } from "@react-oauth/google";
import axios from "axios";
import { GALogEvent } from '/src/services/GoogleAnalytics/GALogEvent';

// HOOKS
import useScrollDepth from './hooks/useScrollDepth';

const siteId = 6410089;
const hotjarVersion = 6;

Hotjar.init(siteId, hotjarVersion);
// Initialize GA4 with configuration
// console.log('import.meta.env.MODE', import.meta.env.MODE);
// console.log('current domain', window.location.hostname);
const currentDomain = window.location.hostname;
const devDomains = ['localhost'];
const testDomains = ['replit'];
const prodDomains = ['getquoll'];
// Check if any devDomain string is in the currentDomain
const isDev = devDomains.some(domain => currentDomain.includes(domain));
const isTest = testDomains.some(domain => currentDomain.includes(domain));
const isProd = prodDomains.some(domain => currentDomain.includes(domain));

const gaId = (isDev || isTest) ? import.meta.env.VITE_GA_ID_DEV : import.meta.env.VITE_GA_ID_PROD;

// GA initialization moved to useEffect in App component to prevent double-initialization

// Create contexts
export const WindowSizeContext = createContext(null);
export const PriceContext = createContext(null);
export const ActiveTabContext = createContext(null);
export const TestStatusContext = createContext();
export const AddressSearchContext = createContext(null);

const getDeviceType = (width) => {
  if (width >= 1024) return "desktop";
  if (width >= 768) return "tablet";
  return "mobile";
};

const getTimestamp = () => {
  return Math.floor(Date.now() / 1000);
}



// Add this right before your Routes component
const RouteDebug = ({ setIsHomePage, LOG_GA_EVENT }) => {
  const location = useLocation();
  // console.log('Routes component sees location:', location);

  useEffect(() => {
    setIsHomePage(location.pathname === '/');
    // Only log page view if GA is initialized
    if (window.gaInitialized) {
      console.log('LOGGING PAGE VIEW FROM ROUTE DEBUG');
      LOG_GA_EVENT('page_view');
    }
    // console.log('Route change detected:', location.pathname);
  }, [location]); // Only trigger when location changes, not when functions change

  return null;
};

// Add this new component after RouteDebug (around line 108)
const ScrollToTopOnRouteChange = () => {
  const location = useLocation();
  const prevMainRouteRef = React.useRef(null);

  // Define main routes that should trigger scroll to top
  // These are the base routes, not sub-routes
  const mainRoutes = [
    '/',
    '/property-intelligence/whats-in-each-analysis',
    '/property-intelligence/common-misconceptions',
    '/about/faq',
    '/about/methodology',
    '/about/what-is-quoll',
    '/about/why-is-quoll-free',
    '/careers',
    '/ccpa-data-request',
    '/contact-us',
    '/do-not-sell',
    '/resources',
  ];

  // Extract the main route from a pathname
  const getMainRoute = (pathname) => {
    // Check if it's a main route exactly
    if (mainRoutes.includes(pathname)) {
      return pathname;
    }

    // Check if it matches a pattern route (like /resources/:slug or /privacy/:slug)
    if (pathname.startsWith('/resources/') && pathname !== '/resources') {
      return '/resources'; // Treat all /resources/* as same main route
    }
    if (pathname.startsWith('/privacy/')) {
      return '/privacy'; // Treat all /privacy/* as same main route
    }

    // Check if it's a property explorer route
    if (pathname.startsWith('/properties/')) {
      // Extract just /properties/:id part (everything before the next segment)
      const match = pathname.match(/^\/properties\/[^/]+/);
      return match ? match[0] : '/properties';
    }

    // For any other route, return the first segment
    const segments = pathname.split('/').filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : '/';
  };

  useEffect(() => {
    const currentMainRoute = getMainRoute(location.pathname);
    const previousMainRoute = prevMainRouteRef.current;

    // Only scroll if we're changing to a different main route
    if (previousMainRoute !== null && currentMainRoute !== previousMainRoute) {
      // Wait for page to be ready, then scroll
      const scrollToTop = () => {
        const scrollContainer = document.querySelector('.content-wrapper');
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      };

      // Try multiple times to ensure the page is ready
      setTimeout(scrollToTop, 100);
      setTimeout(scrollToTop, 500);
      setTimeout(scrollToTop, 1000);
    }

    // Update the ref for next comparison
    prevMainRouteRef.current = currentMainRoute;
  }, [location.pathname]);

  return null;
};

// Scroll Depth Tracker component
const ScrollDepthTracker = ({ LOG_GA_EVENT }) => {
  const location = useLocation();
  useScrollDepth(location.pathname, LOG_GA_EVENT);
  return null;
};

// Add this new component above your App component
const SignInRoute = ({ isLoggedIn, onLogin }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // If already logged in, go back or to home
  if (isLoggedIn) {
    return <Navigate to={location.state?.from || "/"} replace />;
  }

  // Determine which view to show based on the path
  const initialView = location.pathname === '/sign-up' ? 'signup' : 'signin';

  return (
    <Auth
      initialView={initialView}
      onLogin={onLogin}
      handleOutsideClick={() => navigate(location.state?.from || -1)}
      onClose={() => navigate(location.state?.from || -1)}
    />
  );
};

// Simple navigation component
const AddressSearchRouter = ({ targetPath }) => {
  const navigate = useNavigate();

  if (targetPath && targetPath !== window.location.pathname) {
    navigate(targetPath);
  }

  return null;
};

const App = () => {

  // console.log('App component sees location:', location);
  const searchParams = new URLSearchParams(window.location.search);
  const testLogin = searchParams.get('test_login');
  const btid = searchParams.get('btid') || null;

  // Preserve the previous location before the auth modal
  const previousLocation = location.state?.backgroundLocation || location;


  // CREATE THE STATES
  // Navigation
  const [activeTab, setActiveTab] = useState('purchase');
  const [isHomePage, setIsHomePage] = useState(false);

  // Initialize Google Analytics once on mount with guard to prevent double-initialization
  useEffect(() => {
    if (!window.gaInitialized) {
      console.log('Initializing Google Analytics');
      ReactGA4.initialize('G-JYP47W0VZ5', {
        gaOptions: {
          cookie_domain: 'auto'
        },
        gtagOptions: {
          send_page_view: false,
          custom_map: {
            'dimension1': 'quoll_sessionId',
            'dimension2': 'quoll_timestamp',
            'dimension3': 'quoll_is_anonymous'
          }
        }
      });
      window.gaInitialized = true;
    }
  }, []); // Empty dependency array - runs once on mount

  // console.log('isHomePage', isHomePage);
  // User
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [GAEventsQueue, setGAEventsQueue] = useState([]);

  // Refs for idle detection - avoids re-renders and stale closures
  const lastActivityTimeRef = useRef(getTimestamp());
  const userRef = useRef(null);

  // Portal Data
  const [portalData, setPortalData] = useState({
    houseCategories: null,
    icons: null,
    datapoints: null,
    projects: null,
    resources: null,
    companies: null,
    labor_types: null,
    skus: null,
    discounts: null,
    isLoading: true,
    error: null
  });

  // Test Status
  const [testStatus, setTestStatus] = useState(() => {
    return localStorage.getItem('testStatus') === 'true'
  });

  // Address Search
  const addressSearchModelingTrackerInitialState = {
    searchInProgress: false,
    processStage: 'initial',
    searchType: 'input',
    addressDetailsGoogle: null,
    addressDetailsQuoll: null,
    googlePlaceId: null,
    quollPropertyId: null,
    secondaryConfirmationRequired: false,
    secondaryConfirmationReason: null,
    secondaryConfirmationConfirmed: false,
    modelingInProgress: false,
    modelingCompleted: false
  }
  const [addressSearchModelingTracker, setAddressSearchModelingTracker] = useState(addressSearchModelingTrackerInitialState);
  const [showAddressProcessingModal, setShowAddressProcessingModal] = useState(false);
  const [addressSearchInProgress, setAddressSearchInProgress] = useState(false);
  const [addressSearchSecondaryConfirmation, setAddressSearchSecondaryConfirmation] = useState(false);
  const [addressSearchModelingInProgress, setAddressSearchModelingInProgress] = useState(false);
  const [addressSearchGoogleAutocompleteResult, setAddressSearchGoogleAutocompleteResult] = useState(null);
  const [addressSearchModelingCompleted, setAddressSearchModelingCompleted] = useState(false);



  // Property Explorer
  const [skusDiscounts, setSkusDiscounts] = useState(null);

  // Handler for logging GA Events
  const LOG_GA_EVENT = (event, additionalParams = {}) => {

    const quoll_userId_from_additionalParams = additionalParams.quoll_userID;
    const quoll_puid_from_user = user?.puid;

    let quoll_userId_to_use = null;
    if (quoll_userId_from_additionalParams) {
      quoll_userId_to_use = quoll_userId_from_additionalParams;
      // Remove the quoll_userID from the additional params
      delete additionalParams.quoll_userID;
    } else {
      quoll_userId_to_use = user?.user_id || 'Not Signed In';
    }


    // Add sessionId, userID, and timestamp to the additional params
    additionalParams.quoll_sessionId = user?.sessionId;
    additionalParams.quoll_userID = quoll_userId_to_use;
    additionalParams.quoll_timestamp = getTimestamp();
    additionalParams.quoll_btid = btid;
    additionalParams.quoll_site_isDev = isDev;
    additionalParams.quoll_site_isTest = isTest;
    additionalParams.quoll_site_isProd = isProd;
    additionalParams.quoll_puid = quoll_puid_from_user;
    // console.log('LOG_GA_EVENT FROM APP', event, additionalParams);
    // Log the event if there is a user else add to the GA Events Queue
    if (user) {
      GALogEvent({
        eventId: event,
        additionalParams
      });
    } else {
      setGAEventsQueue(prevQueue => [...prevQueue, { event, additionalParams }]);
    }
  }

  // useEffect to watch for changes to user and set the user local storage accordingly
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));

      // If there are events in the GA Events Queue, log them
      if (GAEventsQueue.length > 0) {
        // console.log('LOGGING GA EVENTS FROM QUEUE', GAEventsQueue);
        GAEventsQueue.forEach(event => {
          LOG_GA_EVENT(event.event, event.additionalParams);
        });
        setGAEventsQueue([]);
      }
    }
  }, [user]);

  // Keep userRef in sync for use in idle detection (avoids stale closures)
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  //  useEffect to monitor addressSearchModelingTracker and set the show modal state accordingly
  useEffect(() => {
    if (addressSearchModelingTracker.searchInProgress) {
      setShowAddressProcessingModal(true);
    } else if (showAddressProcessingModal) {  // Only reset if modal was previously shown
      setShowAddressProcessingModal(false);
      setAddressSearchModelingTracker(addressSearchModelingTrackerInitialState);
    }
  }, [addressSearchModelingTracker]);


  //Add useEffect that watched modeling completed and then turns the other states back to false / null
  useEffect(() => {
    if (addressSearchModelingCompleted) {
      setAddressSearchInProgress(false);
      setAddressSearchSecondaryConfirmation(false);
      setAddressSearchModelingInProgress(false);
      setAddressSearchGoogleAutocompleteResult(null);
    }
  }, [addressSearchModelingCompleted]);

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    deviceType: getDeviceType(window.innerWidth),
  });

  // Add effect to check URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promoFromUrl = params.get('promo');

  }, []); // Only run on mount

  const setUserInGa = (inputUser) => {
    // console.log('setUserInGa', inputUser);

    // Set default parameters (will be included in all events)
    // ReactGA4.gtag('config', 'G-JYP47W0VZ5', {
    ReactGA4.gtag('set', {
      quoll_sessionId: inputUser?.sessionId,
      quoll_session_timestamp: inputUser?.timestamp,
      quoll_is_anonymous: inputUser?.isAnonymous
    });

    // Set user properties (for audience segmentation)
    ReactGA4.gtag('set', 'user_properties', {
      QUOLL_SESSION_ID: inputUser?.sessionId,
      quoll_session_timestamp: inputUser?.timestamp,
      quoll_is_anonymous: inputUser?.isAnonymous,
    });

  }

  const generateAnonymousUser = (puid) => {
    const newSessionId = uuidv4();
    const anonymousUser = {
      sessionId: newSessionId,
      timestamp: getTimestamp(),
      isAnonymous: true,
      puid: puid,
      projectsExplainerViewed: false,
      takingActionExplainerViewed: false,
      btid: btid,
    };
    return anonymousUser;
  }

  const createAnonymousSession = () => {
    // Check for existing puid or generate new one
    let puid = localStorage.getItem("quoll_puid");
    if (!puid) {
      puid = uuidv4();
      localStorage.setItem("quoll_puid", puid);
    }

    const existingUser = localStorage.getItem("user");
    if (!existingUser) {

      const anonymousUser = generateAnonymousUser(puid);
      // localStorage.setItem("user", JSON.stringify(anonymousUser));
      setUser(anonymousUser);
      setUserInGa(anonymousUser);
    } else {
      const userData = JSON.parse(existingUser);
      // Add puid to existing user if it doesn't have it
      if (!userData.puid) {
        userData.puid = puid;
      }
      // userData.projectsExplainerViewed = false;
      // userData.takingActionExplainerViewed = false;
      setUser(userData);
      // If the stored user is logged in, set isLoggedIn to true
      if (userData.user_id) {
        setIsLoggedIn(true);
      }
      setUserInGa(userData);
    }
  };

  // Update the initial useEffect to create anonymous session
  useEffect(() => {
    createAnonymousSession();

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setWindowSize({
        width,
        height,
        deviceType: getDeviceType(width),
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const handleLogin = async (userData, eventType = 'login') => {
    const existingUser = JSON.parse(localStorage.getItem("user"));
    const updatedUser = {
      ...existingUser, // Preserve existing session ID and timestamp
      ...userData, // Add user data from login
      isAnonymous: false,
      timestamp: getTimestamp(), // Update timestamp
    };

    // // Add GA4 tracking
    // ReactGA4.event({
    //   category: 'User',
    //   action: 'Login',
    //   label: userData.email,
    //   demo_or_regular: testStatus ? 'Demo' : 'Regular'
    // });

    setUser(updatedUser);
    setUserInGa(updatedUser);
    setIsLoggedIn(true);

    const userId = userData.user_id;
    const userClimateKnowledgeLevel = userData.climate_knowledge;

    const additionalParams = {
      quoll_userID: userId,
      quoll_userClimateKnowledgeLevel: userClimateKnowledgeLevel
    }

    let gaEventId = 'user_login';
    if (eventType === 'signup') {
      gaEventId = 'new_user_signup';
    }

    LOG_GA_EVENT(gaEventId, additionalParams);
  };


  const handleLogout = () => {
    LOG_GA_EVENT('user_logout');

    const existingUser = JSON.parse(localStorage.getItem("user"));
    const existingPuid = localStorage.getItem("quoll_puid");
    const anonymousUser = {
      sessionId: existingUser.sessionId, // Maintain the same session ID
      timestamp: getTimestamp(),
      isAnonymous: true,
      puid: existingPuid
    };

    setUser(anonymousUser);
    setUserInGa(anonymousUser);
    setIsLoggedIn(false);

    // Clear localStorage items (note: quoll_puid is NOT removed)
    localStorage.removeItem("ENCRYPTED_PRICE");
    localStorage.removeItem("PRICE_TIMESTAMP");
    localStorage.removeItem("testStatus");
    localStorage.removeItem("house_categories");
    localStorage.removeItem("icons");
    localStorage.removeItem("qgd");
    localStorage.removeItem("datapoints");

    // Clear IndexedDB stores
    QGDStorage.clearAll();
    ThumbtackStorage.clearAll();
    PortalStorage.clearAll();

    // Clear GA Events Queue
    setGAEventsQueue([]);

    setTestStatus(false);

    // navigate to the home page and reload the page
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  };

  const checkSessionStaleness = () => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    const currentTime = getTimestamp();
    const timeSinceLastActivity = currentTime - lastActivityTimeRef.current;
    const TWELVE_HOURS = 12 * 60 * 60;

    if (timeSinceLastActivity > TWELVE_HOURS) {
      // Session is stale — log out
      handleLogout();
    } else {
      // Session is fine — update the ref (NOT state) to record this activity
      lastActivityTimeRef.current = currentTime;
    }
  };

  // Set up idle detection — runs once, uses refs to avoid teardown/recreation
  useEffect(() => {
    let idleTimer;
    const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        checkSessionStaleness();
      }, TWELVE_HOURS_MS);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // User returned to tab — just check staleness, don't update state
        checkSessionStaleness();
        resetIdleTimer();
      }
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      lastActivityTimeRef.current = getTimestamp();
      resetIdleTimer();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetIdleTimer();

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency array — runs once, reads from refs

  // Add this effect near your other useEffects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
  }, []); // Only run on mount

  // Update localStorage when testStatus changes
  useEffect(() => {
    if (testStatus) {
      localStorage.setItem('testStatus', 'true');
    }
  }, [testStatus]);

  // Update the fetchPortalContent function
  const fetchPortalContent = async () => {
    try {
      // Check what data we have and what's missing
      const { data: storedData, isComplete } = await PortalStorage.getAllDatapoints();

      if (isComplete) {
        setPortalData({
          houseCategories: storedData.house_categories,
          icons: storedData.icons,
          datapoints: storedData.datapoints,
          projects: storedData.projects,
          resources: storedData.resources,
          companies: storedData.companies,
          labor_types: storedData.labor_types,
          skus: storedData.skus,
          discounts: storedData.discounts,
          isLoading: false,
          error: null
        });
        return;
      }

      // If anything is missing, fetch everything fresh
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await axios.get(`${apiBaseUrl}/get-portal-content`);
      const parsedData = parseApiResponse(response.data);
      const validatedData = validatePortalContent(parsedData);

      // Update state immediately so the app can start using the data
      setPortalData({
        houseCategories: validatedData.house_categories,
        icons: validatedData.icons,
        datapoints: validatedData.datapoints,
        projects: validatedData.projects,
        resources: validatedData.resources,
        companies: validatedData.companies,
        labor_types: validatedData.labor_types,
        skus: validatedData.skus || null,
        discounts: validatedData.discounts || null,
        isLoading: false,
        error: null
      });

      // Save all data to IndexedDB asynchronously
      Object.entries(validatedData).forEach(([key, value]) => {
        PortalStorage.setDatapoint(key, value).catch(error => {
          console.error(`Failed to save ${key} to IndexedDB:`, error);
        });
      });

    } catch (error) {
      console.error("Failed to fetch portal content:", error);
      setPortalData(current => ({
        ...current,
        isLoading: false,
        error: error.message
      }));
    }
  };

  // Simplify the useEffect
  useEffect(() => {
    fetchPortalContent();
  }, []); // Run once on mount

  // useEffect that looks for changes to skusDiscounts and updates the portalData
  useEffect(() => {
    if (skusDiscounts) {
      setPortalData(current => ({ ...current, skusDiscounts }));
    }
  }, [skusDiscounts]);

  // Add new useEffect for IndexedDB initialization
  useEffect(() => {
    async function initializeIndexedDB() {
      try {
        // Initialize both stores
        const qgdInitialized = await QGDStorage.init();
        const thumbtackInitialized = await ThumbtackStorage.init();
        const portalInitialized = await PortalStorage.init();
        if (qgdInitialized && thumbtackInitialized && portalInitialized) {
          // Optional: Check if we can read/write to both stores
          const testQGDWrite = await QGDStorage.setData('test', 'init', { timestamp: Date.now() });
          if (testQGDWrite) {
            const testQGDRead = await QGDStorage.getData('test', 'init');
            // console.log('QGD store read/write test successful:', !!testQGDRead);
          }

          const testThumbWrite = await ThumbtackStorage.setListing('test', 'init', { timestamp: Date.now() });
          if (testThumbWrite) {
            const testThumbRead = await ThumbtackStorage.getListing('test', 'init');
            // console.log('Thumbtack store read/write test successful:', !!testThumbRead);
          }

          const testPortalWrite = await PortalStorage.setDatapoint('test', 'init', { timestamp: Date.now() });
          if (testPortalWrite) {
            const testPortalRead = await PortalStorage.getDatapoint('test', 'init');
            // console.log('Portal store read/write test successful:', !!testPortalRead);
          }
        }
      } catch (error) {
        console.error('IndexedDB initialization error:', error);
        // Don't throw - let the app continue with localStorage
      }
    }

    initializeIndexedDB();
  }, []); // Run once on mount

  // handler to mark when a user has viewed either projectsExplainerViewed or takingActionExplainerViewed
  const handleExplainerViewed = (explainer) => {
    // console.log('handleExplainerViewed', explainer);
    if (explainer === 'projectsExplainerViewed') {
      setUser(prevUser => ({ ...prevUser, projectsExplainerViewed: true }));
    } else if (explainer === 'takingActionExplainerViewed') {
      setUser(prevUser => ({ ...prevUser, takingActionExplainerViewed: true }));
    } else if (explainer === 'firstPropertyExplorerViewed') {
      setUser(prevUser => ({ ...prevUser, firstPropertyExplorerViewed: true }));
    }
    // localStorage.setItem('user', JSON.stringify(user));
  }

  // Handler for user opting in to compete over me
  const handleOptInToCompeteOverMe = (propertyId) => {
    const userCompeteOverMeArray = user.competeOverMe || [];
    userCompeteOverMeArray.push(propertyId);
    setUser(prevUser => ({ ...prevUser, competeOverMe: userCompeteOverMeArray }));
  }

  // Handler for letting other pages change the page title
  const handleChangePageTitle = (title) => {

    const container = document.querySelector('.content-wrapper');
    if (container) container.scrollTop = 0;

    document.title = title;
  }




  // console.log('addressSearchModelingTracker FROM APP', addressSearchModelingTracker);
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <WindowSizeContext.Provider value={windowSize}>
          <TestStatusContext.Provider value={{ testStatus, setTestStatus }}>
            <AddressSearchContext.Provider value={{
              addressSearchModelingTracker,
              setAddressSearchModelingTracker,
              addressSearchInProgress,
              setAddressSearchInProgress,
              addressSearchSecondaryConfirmation,
              setAddressSearchSecondaryConfirmation,
              addressSearchModelingInProgress,
              setAddressSearchModelingInProgress,
              addressSearchGoogleAutocompleteResult,
              setAddressSearchGoogleAutocompleteResult,
              addressSearchModelingCompleted,
              setAddressSearchModelingCompleted,
              portalData,
              setSkusDiscounts,
              handleLogout,
              handleExplainerViewed,
              user,
              handleOptInToCompeteOverMe,
              LOG_GA_EVENT,
              handleChangePageTitle
            }}>
              {/* <PriceContext.Provider value={{
              price,
              discountedPrice: calculatedDiscountedPrice,
              discount,
              promoCode,
              promoDetails,
              handlePromoCode
            }}> */}
              <ActiveTabContext.Provider value={{ activeTab, setActiveTab }}>
                <Router>
                  <div className="app">
                    <div className="content-wrapper">
                      <AddressSearchHandler />
                      <PageHeader />
                      {(showAddressProcessingModal) && <AddressSearchProgress />}
                      <RouteDebug setIsHomePage={setIsHomePage} LOG_GA_EVENT={LOG_GA_EVENT} />
                      <ScrollDepthTracker LOG_GA_EVENT={LOG_GA_EVENT} />
                      <ScrollToTopOnRouteChange />

                      {/* <Suspense fallback={<div>Loading...</div>}> */}
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<HomePage />} />
                        <Route path="/property-intelligence" element={<Navigate to="/" replace />} />
                        <Route path="/property-intelligence/whats-in-each-analysis" element={<WhatsInEachAnalysis />} />
                        <Route path="/property-intelligence/common-misconceptions" element={<CommonMisconceptions />} />
                        <Route path="/about" element={<Navigate to="/" replace />} />
                        <Route path="/about/faq" element={<Faq />} />
                        <Route path="/about/methodology" element={<Methodology />} />
                        <Route path="/about/what-is-quoll" element={<WhatIsQuoll />} />
                        <Route path="/about/why-is-quoll-free" element={<WhyFree />} />
                        <Route path="/careers" element={<WorkWithUs />} />
                        <Route path="/ccpa-data-request" element={<ContactUs type="ccpa-data-request" />} />
                        <Route path="/contact-us" element={<ContactUs type="contact-us" />} />
                        <Route path="/do-not-sell" element={<ContactUs type="do-not-sell" />} />
                        <Route path="/privacy/:slug" element={<Blog />} />
                        <Route path="/resources" element={<Resources />} />
                        <Route path="/resources/:slug" element={<Blog />} />
                        {/* <Route path="/purchase" element={<Purchase />} /> */}
                        <Route path="/error" element={<ErrorPage />} />
                        <Route
                          path="/properties"
                          element={
                            <ErrorBoundary>
                              <Navigate to="/" replace />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/properties/:id/*"
                          element={
                            <ErrorBoundary>
                              <PropertyExplorer />
                            </ErrorBoundary>
                          }
                        />
                        {/* <Route path="/contact-us" element={<ContactUs />} /> */}
                        {/* <Route path="/stay-connected" element={<StayConnected />} /> */}

                        {/* Auth Routes */}
                        <Route path="/sign-in" element={<SignInRoute isLoggedIn={isLoggedIn} onLogin={handleLogin} />} />
                        <Route path="/sign-up" element={<SignInRoute isLoggedIn={isLoggedIn} onLogin={handleLogin} />} />

                        {/* Protected Route */}
                        {/* <Route
                        path="/my-account"
                        element={
                          isLoggedIn ? (
                            <MyAccount user={user} onLogout={handleLogout} />
                          ) : (
                            <Navigate to="/sign-in" replace state={{ from: "/my-account" }} />
                          )
                        }
                      /> */}

                        {/* <Route
                          path="/purchase-success"
                          element={<PurchaseSuccess onRefreshPurchases={handleRefreshUserPurchase} />}
                        /> */}

                        {/* Legacy dashboard route redirect */}
                        {/* <Route
                        path="/dashboard/*"
                        element={<Navigate to="/" replace />}
                      /> */}

                        {/* Catch-all route */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                      {/* </Suspense> */}
                      {/* {!isHomePage && <PageFooter />} */}
                      <PageFooter />
                      {/* <PageFooter /> */}
                    </div>
                  </div>
                </Router>
              </ActiveTabContext.Provider>
              {/* </PriceContext.Provider> */}
            </AddressSearchContext.Provider>
          </TestStatusContext.Provider>
        </WindowSizeContext.Provider>
      </GoogleOAuthProvider >
    </HelmetProvider>
  );
};

export default App;
